// Жизненный цикл сессии: вход, восстановление после перезагрузки, обновление, выход.
//
// Обновление токена single-flight: сколько бы запросов ни поймало 401 одновременно, к серверу
// уходит ОДНО обновление, остальные ждут его промис. Иначе первый успевший поворачивает токен
// обновления, а остальные приходят с уже сожжённым и разлогинивают человека посреди работы.

import * as authApi from '@/shared/api/endpoints/auth';
import { setRefreshHandler } from '@/shared/api/client';
import {
  serverAddress,
  serverAddressIsCustom,
  setServerAddress,
} from '@/shared/api/server-address';
import { ApiError } from '@/shared/errors/api-error';

import { clearRefreshToken, readRefreshToken, useAuthStore, writeRefreshToken } from './store';

/** За сколько до истечения токена доступа обновляемся проактивно. */
const REFRESH_AHEAD_MS = 60_000;

/**
 * Кто входил на этой машине в прошлый раз. Не секрет: сами данные этого человека и так лежат
 * в кэше рядом, а идентификатор нужен ровно затем, чтобы понять, СМЕНИЛСЯ ли человек.
 */
const LAST_USER_STORAGE_NAME = 'bark.auth.last-user';

let refreshInFlight: Promise<boolean> | null = null;
let proactiveTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Забыть всё, что накопил кэш запросов.
 *
 * Регистрируется слоем приложения (`app/providers.tsx`) той же связкой, что и обработчик 401:
 * `shared/` не имеет права знать про клиент запросов, иначе получается кольцо импортов.
 *
 * Зачем это вообще. Кэш переживает перезагрузку страницы намеренно — правило 11 требует, чтобы
 * через 0 мс человек видел данные прошлой сессии. Но «прошлая сессия» и «прошлый ЧЕЛОВЕК» —
 * разные вещи, а компьютер в финотделе один на несколько человек. Без этой уборки табельщик,
 * севший за машину после финансового директора, получал на первом кадре пульт подотчёта с ЕГО
 * фамилиями и остатками, да ещё с честной пометкой «данные на 12:03» — то есть картинку,
 * которая выглядит достоверной. Права у нас держатся выборкой на сервере, и здесь выборка
 * отработала верно: её обошёл кэш на диске.
 */
type ForgetHandler = () => void;
let forgetEverything: ForgetHandler | null = null;

export function setForgetHandler(handler: ForgetHandler | null): void {
  forgetEverything = handler;
}

function забыть(): void {
  forgetEverything?.();
}

function прошлыйЧеловек(): string | null {
  try {
    return window.localStorage.getItem(LAST_USER_STORAGE_NAME);
  } catch {
    return null;
  }
}

function запомнитьЧеловека(id: string): void {
  try {
    window.localStorage.setItem(LAST_USER_STORAGE_NAME, id);
  } catch {
    // Хранилища нет (приватный режим, урезанный WebView) — тогда кэш и так не переживает
    // перезагрузку, и забывать между людьми нечего.
  }
}

function cancelProactiveRefresh(): void {
  if (proactiveTimer !== null) {
    clearTimeout(proactiveTimer);
    proactiveTimer = null;
  }
}

function scheduleProactiveRefresh(expiresInSeconds: number): void {
  cancelProactiveRefresh();
  // Не раньше чем через 5 секунд: если сервер выдал токен короче минуты, лупить обновлениями
  // без паузы хуже, чем один раз поймать 401 и обновиться по нему.
  const delay = Math.max(expiresInSeconds * 1000 - REFRESH_AHEAD_MS, 5_000);
  proactiveTimer = setTimeout(() => {
    void refreshSession();
  }, delay);
}

function acceptAuthResponse(response: authApi.AuthResponse): void {
  // Сервер поворачивает токен обновления на каждом обновлении — храним всегда свежий.
  writeRefreshToken(response.refresh_token);
  useAuthStore.getState().setSession({
    accessToken: response.access_token,
    expiresInSeconds: response.expires_in,
    user: response.user,
    demoBanner: response.demo_banner ?? false,
  });
  scheduleProactiveRefresh(response.expires_in);
}

/**
 * Принять новый вход, выданный не входом, а сменой своих данных для входа.
 *
 * Смена гасит ВСЕ входы человека, включая нынешний, и возвращает свежую пару ключей. Без этой
 * строки человек, сменивший себе фразу, через минуту оказывался бы на экране входа — ровно в
 * тот момент, когда он меньше всего уверен, что новая фраза записалась.
 */
export function adoptSession(response: authApi.AuthResponse): void {
  acceptAuthResponse(response);
}

export async function login(loginName: string, password: string): Promise<authApi.UserPayload> {
  const response = await authApi.login({
    login: loginName,
    password,
    device_kind: 'web',
  });
  // Сменился человек — накопленное принадлежит не ему. Тот же человек (перезашёл после
  // истёкшего токена) своё видит сразу, как и требует правило 11.
  if (прошлыйЧеловек() !== response.user.id) {
    забыть();
  }
  запомнитьЧеловека(response.user.id);
  acceptAuthResponse(response);
  return response.user;
}

/**
 * Вход в демонстрацию: показательный сервер с вымышленными людьми и суммами.
 *
 * Сначала переключает адрес, потом входит — иначе запрос уйдёт на сервер организации, где
 * этой двери не существует вовсе. При неудаче адрес возвращается назад: программа, застрявшая
 * на показательном сервере, встретила бы заказчика «нет связи» на его собственных деньгах.
 *
 * Прежний адрес запоминается ровно для этого отката. Выход из программы его не трогает: если
 * человек вышел ИЗ демонстрации, он и должен оказаться на её экране входа, а не на чужом.
 */
export async function loginDemo(адресДемонстрации: string): Promise<authApi.UserPayload> {
  const прежний = serverAddressIsCustom() ? serverAddress() : null;
  setServerAddress(адресДемонстрации);
  try {
    const response = await authApi.demoSession('Демонстрация');
    if (прошлыйЧеловек() !== response.user.id) {
      забыть();
    }
    запомнитьЧеловека(response.user.id);
    acceptAuthResponse(response);
    return response.user;
  } catch (error) {
    setServerAddress(прежний ?? '');
    throw error;
  }
}

/**
 * Обновляет сессию по токену обновления. true — сессия жива, повтор запроса осмысленен.
 * Одновременные вызовы (несколько 401 разом) получают один и тот же промис.
 */
export function refreshSession(): Promise<boolean> {
  if (refreshInFlight === null) {
    refreshInFlight = doRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doRefresh(): Promise<boolean> {
  const stored = readRefreshToken();
  if (stored === null) {
    useAuthStore.getState().setAnonymous();
    return false;
  }
  try {
    const response = await authApi.refresh(stored);
    acceptAuthResponse(response);
    return true;
  } catch (error) {
    if (ApiError.is(error) && !error.isNetworkError) {
      // Сервер явно отверг токен — он сожжён, хранить его дальше незачем.
      clearRefreshToken();
    }
    // Сетевой обрыв токен НЕ стирает: связь вернётся, и вход поднимется без пароля.
    // Но работать в защищённой области без живой сессии нельзя — статус честный.
    useAuthStore.getState().setAnonymous();
    return false;
  }
}

/** Поднимает сессию при старте приложения, если в хранилище лежит токен обновления. */
export function restoreSession(): Promise<boolean> {
  if (readRefreshToken() === null) {
    useAuthStore.getState().setAnonymous();
    return Promise.resolve(false);
  }
  useAuthStore.getState().setRestoring();
  return refreshSession();
}

export async function logout(): Promise<void> {
  cancelProactiveRefresh();
  const stored = readRefreshToken();
  if (stored !== null) {
    try {
      await authApi.logout(stored);
    } catch {
      // Сервер недоступен — выход всё равно состоится локально; токен обновления
      // погаснет на сервере сам по сроку жизни.
    }
  }
  clearRefreshToken();
  useAuthStore.getState().setAnonymous();
  // Выход — это «за эту машину сядет кто-то другой». Данные предыдущего человека уходят с
  // экрана и с диска вместе с ним; иначе следующий вошедший получит на первом кадре чужие
  // фамилии и остатки с достоверной на вид пометкой времени.
  забыть();
}

// Единая точка реакции на 401 в клиенте — это обновление сессии отсюда.
setRefreshHandler(refreshSession);
